import React, { useState } from 'react';
import fr from 'facet-rest';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDIcon, FDPrimaryActionButton, FDSecondaryActionIconButton, Table,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useSequence from 'Hooks/useSequence';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import SimplePicker from 'Helpers/SimplePicker';
import { noop } from 'Helpers/PropTypes';
import { useFilterContext } from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/FilterContext';
import { CURRENT_TIMEFRAME } from 'Organisms/ScenarioChart';
import nextUpFundingInstructionSectionMetaOverrideSet from './metaOverrideSet';

const useStyles = makeStyles(() => ({
  root: {},
  checkbox: {
    margin: 0,
  },
  iconCell: {
    textAlign: 'center',
  },
  checkboxCell: {
    textAlign: 'center',
  },
  loadingIndicator: {
    padding: 0,
  },
}));

const FundingInstructionRow = ({
  resource: resourceProp,
  setActiveTabResourceType,
}) => {
  const classes = useStyles();
  const { displayErrorMessage } = useSnackbarContext();
  const [submitting, setSubmitting] = React.useState(false);
  const [resource, setResource] = React.useState(resourceProp);
  const { loadModalCollectionByLink } = useModalContext();
  const [rowError, setRowError] = React.useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const { setAllocationsTimeframe } = useFilterContext();

  const {
    startSequence,
    isSequenceStale,
    clearSequence,
  } = useSequence();

  const handleSubmit = async () => {
    setSubmitting(true);
    setRowError(false);
    const sequenceId = startSequence();
    try {
      const res = await resource.submit();
      const [updatedRes] = res;
      if (!isSequenceStale(sequenceId)) {
        setResource(updatedRes);
      }
    } catch (err) {
      displayErrorMessage(err, err?.errors[0]?.detail ?? err.errors);
      setRowError(true);
      clearSequence();
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewSource = (relationship) => {
    if (relationship.meta('goto') === 'sac') {
      setAllocationsTimeframe(CURRENT_TIMEFRAME);
      setActiveTabResourceType('sacStepDetails');
      return;
    }

    if (relationship.meta('goto') === 'account-allocations') {
      setAllocationsTimeframe(CURRENT_TIMEFRAME);
      setActiveTabResourceType('accountActionAllocationDetails');
      return;
    }

    // If currently loading, do nothing
    if (loadingSource) {
      return;
    }
    setLoadingSource(true);
    loadModalCollectionByLink(relationship.link('related').url())
      .finally(() => setLoadingSource(false));
  };

  return (
    <Table.BodyRow>
      {!resource.meta().field('hasError').attr('hidden') && (
        <td className={classes.iconCell}>
          {resource.isTruthy('hasError') && (
          <FDIcon icon={'error'} color={'error'} />
          )}
        </td>
      )}
      <td className={classes.iconCell}>
        {resource.withRelationship(fr.relationships.source.name,
          (rel) => (
            <FDSecondaryActionIconButton
              size={'sm'}
              onClick={() => handleViewSource(rel)}
              label={rel.meta('prompt')}
              icon={'visibility'}
              loading={loadingSource}
            />
          ),
          () => null)}
      </td>
      {resource.meta().template().list().map((field) => {
        const fieldName = field.attr('name');
        return !field.attr('hidden') && fieldName !== 'hasError' && (
          <td key={fieldName} className={classNames({ [classes.checkboxCell]: field.attr('type') === 'bool' })}>
            <MetaControl
              className={classNames({ [classes.checkbox]: field.attr('type') === 'bool' })}
              resource={resource}
              attrName={fieldName}
              onChange={(value) => {
                setResource(resource.attr(fieldName, value));
              }}
              onInlineSubmit={handleSubmit}
              metaOverrideSet={nextUpFundingInstructionSectionMetaOverrideSet}
              error={rowError}
            />
          </td>
        );
      })}
      <td>
        <SimplePicker
          resource={resource}
          relationship={fr.relationships.additionalInformation.name}
          hasRelationship={({ relationship }) => (
            <FDPrimaryActionButton
              size={'sm'}
              label={relationship.meta('prompt')}
              onClick={() => {
                loadModalCollectionByLink(relationship.link('related').url());
              }}
            />
          )}
          noRelationship={() => (submitting ? (
            <LoadingIndicator size={20} className={classes.loadingIndicator} />
          ) : null)}
        />
      </td>
    </Table.BodyRow>
  );
};

FundingInstructionRow.propTypes = {
  resource: PropTypes.object.isRequired,
  setActiveTabResourceType: PropTypes.func,
};

FundingInstructionRow.defaultProps = {
  setActiveTabResourceType: noop,
};

export default FundingInstructionRow;
